/**
 * Funzioni di lookup per ottenere i bonus per CAP dal database JSON
 */

import {
  CapBonusDatabase,
  CapBonusEntry,
  CapBonusPV,
  CapBonusHP,
  ProductType,
  BonusDisplayItem,
  CalculatedBonuses,
} from '../types';

// Import del database JSON
import capBonusData from '../../../data/capBonusData.json';

// Interfaccia per i nomi dei bonus dal CSV
interface BonusLabels {
  ecobonus_pv: string;
  pnrr_cer: string;
  conto_termico: string;
  ecobonus_hp: string;
  bonus_regionale_pv: string;
  bonus_regionale_hp: string;
  bonus_provinciale_pv: string;
  bonus_provinciale_hp: string;
  risparmio_bolletta_pv: string;
  risparmio_bolletta_hp: string;
  rivendita_energia: string;
  rivendita_surplus: string;
}

// Interfaccia estesa per il database con labels
interface CapBonusDataWithLabels {
  _labels?: BonusLabels;
  [cap: string]: CapBonusEntry | BonusLabels | undefined;
}

// Cast del database al tipo corretto
const rawData = capBonusData as unknown as CapBonusDataWithLabels;

// Crea il database CAP escludendo _labels
const CAP_DATABASE: CapBonusDatabase = Object.fromEntries(
  Object.entries(rawData).filter(([key]) => key !== '_labels')
) as CapBonusDatabase;

// Estrai i nomi dei bonus dal JSON (dalla riga 3 del CSV)
const BONUS_LABELS: BonusLabels = rawData._labels || {
  ecobonus_pv: 'Ecobonus PV',
  pnrr_cer: 'PNRR CER ENPAL',
  conto_termico: 'Conto Termico',
  ecobonus_hp: 'Ecobonus HP',
  bonus_regionale_pv: 'Bonus Regionale PV',
  bonus_regionale_hp: 'Bonus Regionale HP',
  bonus_provinciale_pv: 'Bonus Provinciale PV',
  bonus_provinciale_hp: 'Bonus Provinciale HP',
  risparmio_bolletta_pv: 'Risparmio in Bolletta PV',
  risparmio_bolletta_hp: 'Risparmio in Bolletta HP',
  rivendita_energia: 'Rivendita Energia',
  rivendita_surplus: 'Rivendita Energia con Surplus CER',
};

/**
 * Pulisce un nome bonus rimuovendo suffissi PV/HP e spazi extra
 */
function cleanBonusLabel(label: string): string {
  return label
    .replace(/\s+PV$/i, '')
    .replace(/\s+HP$/i, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Valori di fallback per CAP non trovati nel database
 */
const FALLBACK_BONUS_PV: CapBonusPV = {
  ecobonus: 9899,
  pnrrCer: 5400,
  pnrrScadenza: '22.11.2026',
  bonusRegionale: null,
  bonusProvinciale: null,
  risparmioBolletta: 23800,
  rivenditaEnergia: 3430,
  rivenditaSurplus: 4189,
};

const FALLBACK_BONUS_HP: CapBonusHP = {
  contoTermico: 4657,
  ecobonus: 7160,
  bonusRegionale: null,
  bonusProvinciale: null,
  risparmioBolletta: 14400,
};

/**
 * Ottiene i dati bonus per un CAP specifico
 * @param cap - Codice postale (5 cifre)
 * @returns Dati del CAP o null se non trovato
 */
export function getCapData(cap: string | undefined): CapBonusEntry | null {
  if (!cap || cap.length !== 5) return null;
  return CAP_DATABASE[cap] || null;
}

/**
 * Ottiene i bonus PV per un CAP, con fallback se non trovato
 */
export function getBonusPVForCap(cap: string | undefined): CapBonusPV {
  const data = getCapData(cap);
  return data?.bonusPV || FALLBACK_BONUS_PV;
}

/**
 * Ottiene i bonus HP per un CAP, con fallback se non trovato
 */
export function getBonusHPForCap(cap: string | undefined): CapBonusHP {
  const data = getCapData(cap);
  return data?.bonusHP || FALLBACK_BONUS_HP;
}

/**
 * Verifica se il CAP Ã¨ in un comune con meno di 50k abitanti
 */
export function isUnder50k(cap: string | undefined): boolean {
  const data = getCapData(cap);
  return data?.abitanti50k ?? true;
}

/**
 * Verifica se il CAP ha bonus regionali/provinciali
 */
export function hasRegionalBonus(cap: string | undefined, productType: ProductType): boolean {
  const data = getCapData(cap);
  if (!data) return false;

  if (productType === 'Photovoltaic' || productType === 'PV_Retrofit') {
    return data.bonusPV.bonusRegionale !== null || data.bonusPV.bonusProvinciale !== null;
  }
  if (productType === 'Heatpump') {
    return data.bonusHP.bonusRegionale !== null || data.bonusHP.bonusProvinciale !== null;
  }
  return (
    data.bonusPV.bonusRegionale !== null ||
    data.bonusPV.bonusProvinciale !== null ||
    data.bonusHP.bonusRegionale !== null ||
    data.bonusHP.bonusProvinciale !== null
  );
}

/**
 * Crea un BonusDisplayItem se il valore Ã¨ valido
 */
function createBonusItem(
  id: string,
  label: string,
  value: number | null,
  scadenza?: string | null
): BonusDisplayItem | null {
  if (value === null || value <= 0) return null;
  return { id, label, value, scadenza };
}

/**
 * Calcola i bonus per Fotovoltaico (PV)
 */
function calculatePVBonuses(bonusPV: CapBonusPV): CalculatedBonuses {
  const allBonus: BonusDisplayItem[] = [];

  const ecobonus = createBonusItem('ecobonus', cleanBonusLabel(BONUS_LABELS.ecobonus_pv), bonusPV.ecobonus);
  const pnrr = createBonusItem('pnrr', cleanBonusLabel(BONUS_LABELS.pnrr_cer), bonusPV.pnrrCer, bonusPV.pnrrScadenza);
  const regionale = createBonusItem('regionale', cleanBonusLabel(BONUS_LABELS.bonus_regionale_pv), bonusPV.bonusRegionale);
  const provinciale = createBonusItem('provinciale', cleanBonusLabel(BONUS_LABELS.bonus_provinciale_pv), bonusPV.bonusProvinciale);
  const bolletta = createBonusItem('bolletta', cleanBonusLabel(BONUS_LABELS.risparmio_bolletta_pv), bonusPV.risparmioBolletta);
  const rivendita = createBonusItem('rivendita', cleanBonusLabel(BONUS_LABELS.rivendita_energia), bonusPV.rivenditaEnergia);
  const surplus = createBonusItem('surplus', cleanBonusLabel(BONUS_LABELS.rivendita_surplus), bonusPV.rivenditaSurplus);

  const regionaleZero =
    bonusPV.bonusRegionale === 0
      ? { id: 'regionale', label: cleanBonusLabel(BONUS_LABELS.bonus_regionale_pv), value: 0 }
      : null;

  if (ecobonus) allBonus.push(ecobonus);
  if (pnrr) allBonus.push(pnrr);
  if (regionale) allBonus.push(regionale);
  if (provinciale) allBonus.push(provinciale);
  if (bolletta) allBonus.push(bolletta);
  if (rivendita) allBonus.push(rivendita);
  if (surplus) allBonus.push(surplus);

  const bonusSenzaBollettaEPnrr = [...allBonus]
    .filter((b) => b.id !== 'bolletta' && b.id !== 'pnrr')
    .sort((a, b) => b.value - a.value);

  let slideBonus: BonusDisplayItem[] = [];

  if (pnrr) {
    slideBonus.push(pnrr);
  }

  for (const bonus of bonusSenzaBollettaEPnrr) {
    if (slideBonus.length >= 4) break;
    slideBonus.push(bonus);
  }

  if (bolletta && slideBonus.length < 4) {
    slideBonus.push(bolletta);
  }

  if (regionaleZero && slideBonus.length < 4) {
    slideBonus.push(regionaleZero);
  }

  const total = allBonus.reduce((sum, b) => sum + b.value, 0);

  return {
    slideBonus,
    summaryBonus: allBonus,
    total,
    bonusCount: allBonus.length,
  };
}

/**
 * Calcola i bonus per Pompa di Calore (HP)
 */
function calculateHPBonuses(bonusHP: CapBonusHP): CalculatedBonuses {
  const allBonus: BonusDisplayItem[] = [];

  const contoTermico = createBonusItem('contotermico', cleanBonusLabel(BONUS_LABELS.conto_termico), bonusHP.contoTermico);
  const ecobonus = createBonusItem('ecobonus', cleanBonusLabel(BONUS_LABELS.ecobonus_hp), bonusHP.ecobonus);
  const regionale = createBonusItem('regionale', cleanBonusLabel(BONUS_LABELS.bonus_regionale_hp), bonusHP.bonusRegionale);
  const provinciale = createBonusItem('provinciale', cleanBonusLabel(BONUS_LABELS.bonus_provinciale_hp), bonusHP.bonusProvinciale);
  const bolletta = createBonusItem('bolletta', cleanBonusLabel(BONUS_LABELS.risparmio_bolletta_hp), bonusHP.risparmioBolletta);

  const regionaleZero = { id: 'regionale', label: cleanBonusLabel(BONUS_LABELS.bonus_regionale_hp), value: 0 };

  if (contoTermico) allBonus.push(contoTermico);
  if (ecobonus) allBonus.push(ecobonus);
  if (regionale) allBonus.push(regionale);
  if (provinciale) allBonus.push(provinciale);
  if (bolletta) allBonus.push(bolletta);

  const bonusSenzaBolletta = [...allBonus]
    .filter((b) => b.id !== 'bolletta')
    .sort((a, b) => b.value - a.value);

  let slideBonus: BonusDisplayItem[];
  if (bonusSenzaBolletta.length >= 4) {
    slideBonus = bonusSenzaBolletta.slice(0, 4);
  } else {
    slideBonus = [...bonusSenzaBolletta];
    if (bolletta && slideBonus.length < 4) {
      slideBonus.push(bolletta);
    }
    if (regionaleZero && slideBonus.length < 4) {
      slideBonus.push(regionaleZero);
    }
  }

  const total = allBonus.reduce((sum, b) => sum + b.value, 0);

  return {
    slideBonus,
    summaryBonus: allBonus,
    total,
    bonusCount: allBonus.length,
  };
}

/**
 * Calcola i bonus per il combo Fotovoltaico + Pompa di Calore
 */
function calculateComboBonuses(bonusPV: CapBonusPV, bonusHP: CapBonusHP): CalculatedBonuses {
  const allBonus: BonusDisplayItem[] = [];

  const pnrr = createBonusItem('pnrr', cleanBonusLabel(BONUS_LABELS.pnrr_cer), bonusPV.pnrrCer, bonusPV.pnrrScadenza);
  if (pnrr) {
    pnrr.category = 'PV';
    allBonus.push(pnrr);
  }

  const contoTermico = createBonusItem('contotermico', cleanBonusLabel(BONUS_LABELS.conto_termico), bonusHP.contoTermico);
  if (contoTermico) {
    contoTermico.category = 'HP';
    allBonus.push(contoTermico);
  }

  const ecobonusPV = createBonusItem('ecobonus_pv', cleanBonusLabel(BONUS_LABELS.ecobonus_pv), bonusPV.ecobonus);
  if (ecobonusPV) {
    ecobonusPV.category = 'PV';
    allBonus.push(ecobonusPV);
  }

  const ecobonusHP = createBonusItem('ecobonus_hp', cleanBonusLabel(BONUS_LABELS.ecobonus_hp), bonusHP.ecobonus);
  if (ecobonusHP) {
    ecobonusHP.category = 'HP';
    allBonus.push(ecobonusHP);
  }

  const rivendita = createBonusItem('rivendita', cleanBonusLabel(BONUS_LABELS.rivendita_energia), bonusPV.rivenditaEnergia);
  if (rivendita) {
    rivendita.category = 'PV';
    allBonus.push(rivendita);
  }

  const surplus = createBonusItem('surplus', cleanBonusLabel(BONUS_LABELS.rivendita_surplus), bonusPV.rivenditaSurplus);
  if (surplus) {
    surplus.category = 'PV';
    allBonus.push(surplus);
  }

  const bollettaPV = createBonusItem('bolletta_pv', cleanBonusLabel(BONUS_LABELS.risparmio_bolletta_pv), bonusPV.risparmioBolletta);
  if (bollettaPV) {
    bollettaPV.category = 'PV';
    allBonus.push(bollettaPV);
  }

  const bollettaHP = createBonusItem('bolletta_hp', cleanBonusLabel(BONUS_LABELS.risparmio_bolletta_hp), bonusHP.risparmioBolletta);
  if (bollettaHP) {
    bollettaHP.category = 'HP';
    allBonus.push(bollettaHP);
  }

  const regionalePV = createBonusItem('regionale_pv', cleanBonusLabel(BONUS_LABELS.bonus_regionale_pv), bonusPV.bonusRegionale);
  if (regionalePV) {
    regionalePV.category = 'PV';
    allBonus.push(regionalePV);
  }

  const regionaleHP = createBonusItem('regionale_hp', cleanBonusLabel(BONUS_LABELS.bonus_regionale_hp), bonusHP.bonusRegionale);
  if (regionaleHP) {
    regionaleHP.category = 'HP';
    allBonus.push(regionaleHP);
  }

  const provincialePV = createBonusItem('provinciale_pv', cleanBonusLabel(BONUS_LABELS.bonus_provinciale_pv), bonusPV.bonusProvinciale);
  if (provincialePV) {
    provincialePV.category = 'PV';
    allBonus.push(provincialePV);
  }

  const provincialeHP = createBonusItem('provinciale_hp', cleanBonusLabel(BONUS_LABELS.bonus_provinciale_hp), bonusHP.bonusProvinciale);
  if (provincialeHP) {
    provincialeHP.category = 'HP';
    allBonus.push(provincialeHP);
  }

  const regionaleZeroPV =
    bonusPV.bonusRegionale === 0
      ? {
          id: 'regionale',
          label: cleanBonusLabel(BONUS_LABELS.bonus_regionale_pv),
          value: 0,
          category: 'PV' as const,
        }
      : null;
  const regionaleZeroHP =
    bonusHP.bonusRegionale === 0
      ? {
          id: 'regionale',
          label: cleanBonusLabel(BONUS_LABELS.bonus_regionale_hp),
          value: 0,
          category: 'HP' as const,
        }
      : null;
  const regionaleZero = regionaleZeroPV || regionaleZeroHP;

  const bonusSenzaBollettaEPnrr = [...allBonus]
    .filter((b) => !b.id.startsWith('bolletta') && b.id !== 'pnrr')
    .sort((a, b) => b.value - a.value);

  const bollettaFallback =
    bollettaPV && bollettaHP
      ? bollettaPV.value >= bollettaHP.value
        ? bollettaPV
        : bollettaHP
      : bollettaPV || bollettaHP;

  let slideBonus: BonusDisplayItem[] = [];

  if (pnrr) {
    slideBonus.push(pnrr);
  }

  for (const bonus of bonusSenzaBollettaEPnrr) {
    if (slideBonus.length >= 4) break;
    slideBonus.push(bonus);
  }

  if (bollettaFallback && slideBonus.length < 4) {
    slideBonus.push(bollettaFallback);
  }

  if (regionaleZero && slideBonus.length < 4) {
    slideBonus.push(regionaleZero);
  }

  const total = allBonus.reduce((sum, b) => sum + b.value, 0);

  return {
    slideBonus,
    summaryBonus: allBonus,
    total,
    bonusCount: allBonus.length,
  };
}

/**
 * Funzione principale: calcola tutti i bonus per un CAP e tipo di prodotto
 */
export function getBonusesForCap(
  cap: string | undefined,
  productType: ProductType | undefined
): CalculatedBonuses {
  const bonusPV = getBonusPVForCap(cap);
  const bonusHP = getBonusHPForCap(cap);

  switch (productType) {
    case 'Photovoltaic':
    case 'PV_Retrofit':
      return calculatePVBonuses(bonusPV);
    case 'Heatpump':
      return calculateHPBonuses(bonusHP);
    case 'Photovoltaic + Heatpump':
      return calculateComboBonuses(bonusPV, bonusHP);
    default:
      return calculatePVBonuses(bonusPV);
  }
}

export function getBonusTextForSlide(
  cap: string | undefined,
  productType: ProductType | undefined,
  slideIndex: number
): { label: string; value: number; scadenza?: string | null } | null {
  const bonuses = getBonusesForCap(cap, productType);
  const bonus = bonuses.slideBonus[slideIndex];

  if (!bonus) return null;

  return {
    label: bonus.label,
    value: bonus.value,
    scadenza: bonus.scadenza,
  };
}

export function formatBonusForDisplay(value: number, label: string): string {
  const formatted = new Intl.NumberFormat('de-DE').format(value);
  return `+${formatted}€ tramite ${label}`;
}

export function getTooltipTextForProduct(productType: ProductType | undefined): string {
  switch (productType) {
    case 'Photovoltaic':
    case 'PV_Retrofit':
      return 'calcolato sulla base del nostro impianto best-seller: Impianto Fotovoltaico da 9kW + 16kW di Batteria';
    case 'Heatpump':
      return 'calcolato sulla base del nostro impianto best-seller: Pompa di calore 12kW full-electric';
    case 'Photovoltaic + Heatpump':
      return 'calcolato sulla base del nostro impianto best-seller: Impianto Fotovoltaico da 9kW + 16kW di Batteria & Pompa di calore 12kW full-electric';
    default:
      return 'calcolato sulla base del nostro impianto best-seller: Impianto Fotovoltaico da 9kW + 16kW di Batteria';
  }
}
